/**
 * checkoutLoaderContent.ts — what the checkout's "Getting your order ready"
 * loader shows while it waits in front of the plan screen, and how long it may
 * keep the patient reading (CTO, 2026-09-25).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: checkout, the pre-plan
 * loader (it shows the journey entry loader's card, `ui/LoaderJourneyCard.tsx`,
 * with a checkout journey).
 *
 * THE ASKS, verbatim. With a screenshot of this loader: "Even for this loader
 * that comes before selecting my plan, I think we can do something smarter to
 * use that for some engaging content there till the next page loads. Have a
 * set loading time period." Then, on the entry loader's card (~18:00): "Yes,
 * you can make the card grow as the items appear, but I think right now it's
 * too much text. Let's try to use more icons if possible … use very catchy
 * text … grab the attention because we have a very short window here." And
 * earlier: no carousel, no pause; load one after the other, showing the
 * journey; keep Switch & Save.
 *
 * ── THE SAME CARD, THE REST OF THE JOURNEY ─────────────────────────────────
 * `BrandedLoader`'s journey look draws the logo ring and the status line; under
 * them the SAME icon-led card the entry loader shows (`LoaderJourneyCard`: each
 * item an icon in a soft disc, a 2–5 word headline and at most one short line,
 * revealed one after another, the card growing as they arrive, then static; no
 * control; reduced motion: everything at once). Only the words differ: the
 * entry card shows how the whole thing works, this one shows what is LEFT —
 * under the Result screen's own tracker line ("Just a few steps left"), the
 * checkout in the order its screens run, then the same three chips:
 *
 *   heading  `RESULT_STEPS_LEFT` (the Result tracker's line, verbatim)
 *   1. [checklist]    {@link CHOOSE_TREATMENT_AND_PLAN} — the plan screen's two choices
 *   2. [parcel]       `FREE_DELIVERY` · {@link IN_PLAIN_PACKAGING}
 *   3. [card]         `CARD_HELD_NOT_CHARGED` · `NO_CHARGE_UNTIL_APPROVED_SHORT`
 *   4. [stethoscope]  `CLINICIAN_REVIEW_SHORT` · `REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY`
 *   chips: the entry card's own three objects — "Free call back", "Switch &
 *   Save — typically 8–10% less than …" (the figure is `SWITCH_SAVE_RANGE`,
 *   never retyped), "Our team is here to help".
 *
 * ── SHORT AND CATCHY, BUT NEVER A NEW CLAIM (DMCC Act 2024 / CAP Code 3.1,
 * 3.7 and s.13; R28) ─────────────────────────────────────────────────────────
 * Every line restates a claim the site ALREADY makes, and says no more than it:
 * a constant another screen prints, imported by name, or a SHORT FORM that lives
 * beside the long claim it restates (`planStepCopy.ts`, `deliveryCopy.ts`,
 * `paymentCopy.ts`, `journey/serviceClaims.ts`) or here with its source named on
 * it. The copy table (CHANGELOG 2026-09-25, "the checkout loader") maps each
 * line to its source file and line; `checkoutLoaderContent.spec` pins each short
 * form against its source, so a reword of the source turns it red. What the card
 * may never carry: a rating, a statistic, a speed beyond "usually within one
 * working day", "£0 today" (a HOLD is placed — the claim is "no charge until
 * approved"), a delivery figure or saving (DMCC: `deliveryCopy.ts`), an outcome
 * or weight promise, a medicine or delivery-form word, and "discreet" (the
 * Result screen's word; this card says what it means, "in plain packaging").
 * "Any time" is left off the call back: no patient-facing line says it.
 *
 * ── ONLY WHERE EVERY LINE IS TRUE ───────────────────────────────────────────
 * Two lines are claims about THIS checkout: that the next screen is the plan
 * chooser, and that the card is only held. Neither is true everywhere this
 * loader shows — a plan link can be the clinician-led route (confirming there
 * takes the first month at once), and a resume can land on delivery, payment
 * or the account step. So, like the entry card (`journeyLoaderCard`: a fresh
 * start only), the card shows on ONE lane, chosen by the page that knows it:
 * JourneyPage passes `preplanLoaderContent` only when the checkout was opened
 * from the Result screen of an assessment THIS document just submitted — the
 * self-serve lane, which always takes the hold route, with no plan chosen yet.
 * Everywhere else the loader is the plain mark and line it always was.
 *
 * ── THE SET LOADING TIME: {@link CHECKOUT_PREPLAN_MIN_DWELL_MS} ─────────────
 * On that lane, when the restore RESOLVES to the plan chooser (nothing
 * committed: orders answered `none` and no clinician-built plan exists), the
 * loader stays until its mark has been drawn for the minimum — `useLoaderHold`,
 * counted from the draw (after the 300ms show delay), never from the click —
 * and the card has finished building up well inside it
 * ({@link CHECKOUT_LOADER_REVEALED_BY_MS}). Every other resolution releases AT
 * ONCE, exactly as before: an error (REQUIREMENTS A8, a loader never delays an
 * error), a 3D Secure return (#458's `finishRedirectReturn` lane, under its own
 * "Finishing your order"), a resume onto delivery / payment / the account step,
 * a paid or held order, any other committed order state, a clinician-built plan,
 * and the signed-out path that lands on the plan with no read at all. And it
 * applies to the checkout's ENTRY wait only: a later re-read (after a refusal, a
 * sign-in elsewhere) is never held and carries no card.
 *
 * ── …RUNNING ALONGSIDE THE PLAN SCREEN'S OWN READS, "TILL THE NEXT PAGE
 * LOADS" (release review, 2026-09-25) ────────────────────────────────────────
 * The landing on the chooser STARTS the plan screen's reads (plan options, then
 * the clinician recommendation — `planScreenRead.ts`) instead of leaving them
 * to the plan screen's mount after the hold, and on this lane they are part of
 * the wait: the loader goes at max(the minimum from the draw, the reads
 * landing), and the plan screen opens on their answer, so the card is never
 * followed by a "Fetching your plan options…" skeleton. A read that FAILS lets
 * the loader go at once and its error shows (A8 again). Measured before the
 * fix: the reads sat idle behind the hold for ~2.3s.
 *
 * ⚠️ A LOADER THAT NEVER DREW IS NEVER HELD — A DECISION FOR THE CTO (2026-09-25).
 * A wait (the restore and, on this lane, the plan screen's reads behind it)
 * that ends inside the 300ms show delay has shown nothing, so nothing is held
 * and the plan screen comes straight up. The other reading — a hold
 * counted from the click, so everyone waits ~3s — would make a fast checkout
 * slow ON PURPOSE to show a card the patient had not asked for, and would
 * bring back the flash the show delay exists to prevent. "A set loading time
 * period" is read as: once the content is on screen, it stays long enough to
 * read; it never adds a wait where there was none.
 *
 * ⚠️ THE SWITCH & SAVE CHIP carries the entry card's open CTO item with it (CAP
 * Code 3.7: the 8–10% has no dated competitor basket behind it — see
 * `journey/entryLoaderContent.ts` and `switchAndSaveCopy.ts`). This screen is
 * post-eligibility, so it is not a wider audience than the entry card's; pulling
 * it here is one line — drop `SWITCH_SAVE_CHIP` from {@link CHECKOUT_LOADER_CHIPS}.
 */
import { CALLBACK_CHIP, SWITCH_SAVE_CHIP, TEAM_SUPPORT_CHIP } from '../journey/entryLoaderContent';
import { RESULT_STEPS_LEFT } from '../journey/resultCopy';
import {
  CLINICIAN_REVIEW_SHORT,
  NO_CHARGE_UNTIL_APPROVED_SHORT,
  REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY,
} from '../journey/serviceClaims';
import { revealDoneMs, type LoaderJourney, type LoaderJourneyChip, type LoaderJourneyStep } from '../ui/LoaderJourneyCard';
import { FREE_DELIVERY } from './deliveryCopy';
import { CARD_HELD_NOT_CHARGED } from './paymentCopy';
import { CHOOSE_TREATMENT_AND_PLAN } from './planStepCopy';

/**
 * THE SET LOADING TIME (see the header): once the loader's mark is DRAWN, the
 * pre-plan lane keeps it up for at least this long. Long enough for the whole
 * card to build up AND be read for a moment
 * ({@link CHECKOUT_LOADER_REVEALED_BY_MS} is ~2.55s after the draw), short
 * enough that 300ms + this ends before the 4s "still working" line could start.
 * Pinned by `checkoutLoaderContent.spec.tsx` and, on a fake clock through the
 * real CheckoutFlow, by `preplanLoader.spec.tsx`.
 */
export const CHECKOUT_PREPLAN_MIN_DWELL_MS = 3000;

/**
 * Step 2's line. SOURCE, word by word: the celebration's hold trust point,
 * "Once approved, your treatment is sent to your door in plain packaging."
 * (`celebrationCopy.ts`, `celebrationCopy('hold').trust[1].body`) — its last
 * three words, none added — and the Result screen's delivery step says the same
 * ("Sent to your door in plain, insulated packaging.", `ResultScreen.tsx`).
 * `checkoutLoaderContent.spec` pins both.
 */
export const IN_PLAIN_PACKAGING = 'In plain packaging';

/** The rest of the journey, in the order the checkout's screens run it. */
export const CHECKOUT_LOADER_STEPS: ReadonlyArray<LoaderJourneyStep> = [
  { id: 'plan', icon: 'choose', title: CHOOSE_TREATMENT_AND_PLAN },
  { id: 'delivery', icon: 'parcel', title: FREE_DELIVERY, detail: IN_PLAIN_PACKAGING },
  { id: 'card', icon: 'card', title: CARD_HELD_NOT_CHARGED, detail: NO_CHARGE_UNTIL_APPROVED_SHORT },
  { id: 'review', icon: 'clinician', title: CLINICIAN_REVIEW_SHORT, detail: REVIEW_USUALLY_WITHIN_ONE_WORKING_DAY },
];

/**
 * The chips: the entry card's own three objects, in its order (a free call
 * back, Switch & Save — kept, CTO — and our team), so one reword moves both
 * cards. See the ⚠️ on Switch & Save in the header.
 */
export const CHECKOUT_LOADER_CHIPS: ReadonlyArray<LoaderJourneyChip> = [CALLBACK_CHIP, SWITCH_SAVE_CHIP, TEAM_SUPPORT_CHIP];

/**
 * The whole card, as `BrandedLoader`'s journey look takes it. A module
 * constant, so a re-render never hands the card a new object.
 */
export const CHECKOUT_LOADER_CARD: LoaderJourney = {
  heading: RESULT_STEPS_LEFT,
  steps: CHECKOUT_LOADER_STEPS,
  chips: CHECKOUT_LOADER_CHIPS,
};

/**
 * WHEN THE WHOLE CARD IS ON SCREEN, from the loader's DRAW (the card appears
 * with the mark): the first item with the card, one every `REVEAL_STEP_MS`, the
 * last fully grown in — 6 × 350 + 450 = 2550ms. Inside
 * {@link CHECKOUT_PREPLAN_MIN_DWELL_MS} by design, so a held load always shows
 * every item before the plan screen replaces it (pinned).
 */
export const CHECKOUT_LOADER_REVEALED_BY_MS = revealDoneMs(CHECKOUT_LOADER_STEPS.length + CHECKOUT_LOADER_CHIPS.length);
